import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Contract {
  id: string;
  clientId: string;
  clientName: string;
  rateType: string;
  paymentTerms: string;
  creditDays: number;
  discount: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'expiring' | 'expired';
  services: string[];
}

export default function ContractsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  const contracts: Contract[] = [
    {
      id: '1',
      clientId: 'CLI-001',
      clientName: 'Importadora Global S.A.',
      rateType: 'Tarifa Preferencial A',
      paymentTerms: 'Crédito',
      creditDays: 30,
      discount: 5,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      status: 'active',
      services: ['Despacho Aduanero', 'Transporte Terrestre', 'Almacenaje']
    },
    {
      id: '2',
      clientId: 'CLI-002',
      clientName: 'Exportadora del Pacífico',
      rateType: 'Tarifa Estándar',
      paymentTerms: 'Crédito',
      creditDays: 45,
      discount: 3,
      startDate: '2023-06-01',
      endDate: '2024-05-31',
      status: 'expiring',
      services: ['Despacho Aduanero', 'Gestión Documental']
    },
    {
      id: '3',
      clientId: 'CLI-003',
      clientName: 'Logística Continental',
      rateType: 'Tarifa Corporativa',
      paymentTerms: 'Crédito',
      creditDays: 60,
      discount: 10,
      startDate: '2024-02-01',
      endDate: '2025-01-31',
      status: 'active',
      services: ['Despacho Aduanero', 'Transporte Terrestre', 'Almacenaje', 'Seguro de Carga']
    },
    {
      id: '4',
      clientId: 'CLI-004',
      clientName: 'Comercial Centroamericana',
      rateType: 'Tarifa Estándar',
      paymentTerms: 'Contado',
      creditDays: 0,
      discount: 0,
      startDate: '2024-03-01',
      endDate: '2024-08-31',
      status: 'active',
      services: ['Despacho Aduanero']
    }
  ];

  const getStatusBadge = (status: string) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      expiring: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800'
    };
    const labels = {
      active: 'Activo',
      expiring: 'Por Renovar',
      expired: 'Vencido'
    };
    return { class: badges[status as keyof typeof badges], label: labels[status as keyof typeof labels] };
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Contratos y Condiciones</h1>
            <p className="text-sm text-gray-500 mt-1">Gestión de condiciones comerciales por cliente</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Contrato
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Contratos Activos</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">3</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-text-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Por Renovar</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">1</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-alarm-warning-line text-2xl text-yellow-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Crédito Promedio</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">34 días</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Descuento Promedio</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">4.5%</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-percent-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de Contratos */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Contratos Registrados</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo Tarifa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condición Pago</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Días Crédito</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Descuento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vigencia</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contracts.map((contract) => {
                  const statusBadge = getStatusBadge(contract.status);
                  return (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{contract.clientName}</p>
                          <p className="text-xs text-gray-500">{contract.clientId}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contract.rateType}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{contract.paymentTerms}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          {contract.creditDays} días
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                          {contract.discount}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(contract.startDate).toLocaleDateString('es-GT')} - {new Date(contract.endDate).toLocaleDateString('es-GT')}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedContract(contract);
                            setShowDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <button className="text-gray-600 hover:text-gray-800">
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Crear Contrato */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Nuevo Contrato</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Seleccionar cliente</option>
                      <option>Importadora Global S.A.</option>
                      <option>Exportadora del Pacífico</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Tarifa *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Seleccionar tarifa</option>
                      <option>Tarifa Estándar</option>
                      <option>Tarifa Preferencial A</option>
                      <option>Tarifa Corporativa</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condición de Pago *</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option>Contado</option>
                      <option>Crédito</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Días de Crédito</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descuento (%)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio *</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin *</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Servicios Incluidos</label>
                  <div className="space-y-2">
                    {['Despacho Aduanero', 'Transporte Terrestre', 'Almacenaje', 'Gestión Documental', 'Seguro de Carga'].map(service => (
                      <label key={service} className="flex items-center">
                        <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                        <span className="ml-2 text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Crear Contrato
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Detalle */}
        {showDetailModal && selectedContract && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Detalle del Contrato</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Información del Cliente</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Cliente</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.clientName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Código</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.clientId}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Condiciones Comerciales</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Tipo de Tarifa</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.rateType}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Condición de Pago</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.paymentTerms}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Días de Crédito</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.creditDays} días</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Descuento</p>
                      <p className="text-sm font-medium text-gray-900">{selectedContract.discount}%</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Vigencia</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Fecha Inicio</p>
                      <p className="text-sm font-medium text-gray-900">{new Date(selectedContract.startDate).toLocaleDateString('es-GT')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Fecha Fin</p>
                      <p className="text-sm font-medium text-gray-900">{new Date(selectedContract.endDate).toLocaleDateString('es-GT')}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Servicios Incluidos</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedContract.services.map(service => (
                      <span key={service} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Documentos</h4>
                  <button className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                    <i className="ri-file-pdf-line"></i>
                    <span className="text-sm">Descargar Contrato.pdf</span>
                  </button>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Editar Contrato
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
